import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link, useParams, useNavigate } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import './Articles.css';
import { getItemTitleList, getProjectCategory, getItemList, getItemListSizeMultiLang } from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import { getLocalizedItemLabels, itemHasLocaleRow } from '../../utils/itemLocaleDisplay';
import { getLocalizedCategoryName } from '../../utils/categoryLocaleDisplay';
import {
  getRootCategories,
  getCategoryNavLevels,
  isCategoryInSelection,
  filterItemsByCategory,
  categoryHasMatchingItems,
} from '../../utils/categoryTree';
import { mergeLangArrays, fetchItemLangMapsByIds } from '../../utils/itemLangRows';
import { filterItemsByCity, collectCityOptions, mergeCityById } from '../../utils/itemCity';
import { TURKEY_PROVINCE_NAMES } from '../../utils/turkeyProvinceCenters';

/** Anasayfa ile aynı multiLang uç; önce hızlı dolgu (tam katalog değilse bile yararlı). */
const ENTRIES_MULTILANG_MERGE_CAP = 8000;

/** Detay sayfasıyla aynı itemLang ucu — eksik çevirileri toplu tamamlar */
const ITEM_LANG_FETCH_CONCURRENCY = 12;

/**
 * Harf sekmesi anahtarı: TR’de İ/I ayrımı; EN/FR’de aksanı kaldırarak Latin harfe yaklaştırır.
 */
function groupingInitial(displayTitle, uiLocale) {
  const raw = (displayTitle || '').trim();
  if (!raw) return '';
  const ch = raw[0];
  if (uiLocale === 'tr') {
    return ch.toLocaleUpperCase('tr-TR');
  }
  const loc = uiLocale === 'fr' ? 'fr-FR' : 'en-GB';
  const upper = ch.toLocaleUpperCase(loc);
  const stripped = upper.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return stripped ? stripped.charAt(0).toUpperCase() : upper;
}

const ALPHABET = ['A', 'B', 'C', 'Ç', 'D', 'E', 'F', 'G', 'Ğ', 'H', 'I', 'İ', 'J', 'K', 'L', 'M', 'N', 'O', 'Ö', 'P', 'R', 'S', 'Ş', 'T', 'U', 'Ü', 'V', 'Y', 'Z'];
const NUMBERS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '(', ')'];
const LETTER_GROUPS = {
  'A-E': ['A', 'B', 'C', 'Ç', 'D', 'E'],
  'F-J': ['F', 'G', 'Ğ', 'H', 'I', 'İ', 'J'],
  'K-P': ['K', 'L', 'M', 'N', 'O', 'Ö', 'P'],
  'R-Z': ['R', 'S', 'Ş', 'T', 'U', 'Ü', 'V', 'Y', 'Z'],
  '0-9': NUMBERS,
};

const Articles = () => {
  const { t, locale } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [selectedLetter, setSelectedLetter] = useState('A');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showAllArticles, setShowAllArticles] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

 
  const params = useParams()
	const {catId} = params; 
  const selectedCity = new URLSearchParams(location.search).get('city') || '';
  const [allItems, setAllItems] = useState([]);
  /** Tüm kategori ağacı (parentId hiyerarşisi) */
  const [allCategories, setAllCategories] = useState([]);

  const getAPI = async () => {
    setLoading(true);
    try {
      const [resSlider, resCat, resFull] = await Promise.all([
        getItemTitleList(),
        getProjectCategory(),
        getItemList().catch((err) => {
          console.warn('Maddeler itemPublic listesi alınamadı:', err);
          return null;
        }),
      ]);

      const allCats = resCat?.status && Array.isArray(resCat.data) ? resCat.data : [];
      setAllCategories(allCats);

      if (!resSlider?.status || !Array.isArray(resSlider.data)) {
        setAllItems([]);
        return;
      }

      let items = resSlider.data;
      /** title/ city taşımayabilir; itemPublic/ kök listesinden id ile birleştirilir. */
      const fullListData = resFull?.status && Array.isArray(resFull.data) ? resFull.data : null;

      /*
       * title/ lang taşımıyor.
       * 1) multiLang(size) — anasayfadaki gibi lang[] (çoğunlukla son eklenenler).
       * 2) Tam liste — bazı kayıtlarda gömülü lang.
       * 3) Hâlâ seçilen dil için satır yoksa ve çok dilli madde ise itemLang/id (detay ile aynı kaynak).
       */
      if (locale !== 'tr') {
        const langById = new Map();
        /** @type {Map<number, { isMultiLang?: number }>} */
        const metaById = new Map();

        const accumulateLang = (itemId, langArr) => {
          if (itemId == null || !Array.isArray(langArr) || langArr.length === 0) return;
          const prev = langById.get(itemId);
          langById.set(itemId, prev?.length ? mergeLangArrays(prev, langArr) : langArr);
        };

        try {
          const mlRes = await getItemListSizeMultiLang(ENTRIES_MULTILANG_MERGE_CAP);
          if (mlRes?.status && Array.isArray(mlRes.data)) {
            for (const row of mlRes.data) {
              accumulateLang(row?.id, row?.lang);
            }
          }
        } catch (mergeErr) {
          console.warn('Maddeler multiLang birleştirme yapılamadı:', mergeErr);
        }

        if (fullListData) {
          for (const row of fullListData) {
            if (row?.id == null) continue;
            metaById.set(row.id, { isMultiLang: row.isMultiLang });
            accumulateLang(row.id, row.lang);
          }
        }

        items = items.map((row) => {
          const langMerged = mergeLangArrays(
            langById.has(row.id) ? langById.get(row.id) : [],
            Array.isArray(row.lang) ? row.lang : []
          );
          const meta = metaById.get(row.id);
          return {
            ...row,
            ...(meta ? { isMultiLang: meta.isMultiLang } : {}),
            ...(langMerged.length ? { lang: langMerged } : {}),
          };
        });

        const idsNeedingItemLang = items
          .filter((row) => {
            if (itemHasLocaleRow(row, locale)) return false;
            const m = metaById.get(row.id)?.isMultiLang;
            /* 0 = tek dil; diğer tüm durumlarda (1, undefined, meta yok) itemLang dene — detay sayfası verisi */
            return m !== 0;
          })
          .map((row) => row.id);

        if (idsNeedingItemLang.length > 0) {
          const extraLangById = await fetchItemLangMapsByIds(idsNeedingItemLang, ITEM_LANG_FETCH_CONCURRENCY);
          items = items.map((row) => {
            const extra = extraLangById.get(row.id);
            if (!extra?.length) return row;
            const merged = mergeLangArrays(Array.isArray(row.lang) ? row.lang : [], extra);
            return { ...row, lang: merged };
          });
        }
      }

      items = mergeCityById(items, fullListData, TURKEY_PROVINCE_NAMES);
      if (fullListData && !items.some((item) => item.city) && fullListData.some((row) => row.city)) {
        const titleById = new Map(items.map((row) => [row.id, row]));
        items = fullListData.map((row) => ({
          ...(titleById.get(row.id) || row),
          city: row.city,
        }));
      }

      setAllItems(items);
    } catch (e) {
      console.error('Maddeler yüklenemedi:', e);
      setAllItems([]);
      setAllCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAPI();
  }, [locale]);

  // URL'den gelen state'i kontrol et
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const group = params.get('group');
    if (group && LETTER_GROUPS[group]) {
      setSelectedGroup(group);
      setSelectedLetter(LETTER_GROUPS[group][0]);
      setShowAllArticles(false);
    } else {
      // Eğer herhangi bir group parametresi yoksa, tüm maddeler gösterilecek
      setSelectedGroup(null);
      setSelectedLetter('A');
      setShowAllArticles(true);
    }
  }, [location]);

  const handleLetterClick = (letter) => {
    setSelectedLetter(letter);
    setShowAllArticles(false);
  };

  const entriesPath = (pathname, extra = {}) => {
    const next = new URLSearchParams(location.search);
    Object.entries(extra).forEach(([key, value]) => {
      if (value == null || value === '') next.delete(key);
      else next.set(key, value);
    });
    const query = next.toString();
    return query ? `${pathname}?${query}` : pathname;
  };

  const handleCityChange = (event) => {
    const city = event.target.value;
    setSelectedGroup(null);
    setShowAllArticles(true);
    navigate(entriesPath(location.pathname, { city }), { replace: true });
  };

  const cityItems = useMemo(
    () => filterItemsByCity(allItems, selectedCity, TURKEY_PROVINCE_NAMES),
    [allItems, selectedCity]
  );

  const data = useMemo(() => {
    const items =
      catId === undefined
        ? cityItems
        : filterItemsByCategory(cityItems, allCategories, catId);

    const bucketKey = (item) =>
      groupingInitial(
        getLocalizedItemLabels(item, locale).title || item.title || '',
        locale
      );

    const groupedData = {};
    ALPHABET.forEach((letter) => {
      const letterItems = items.filter((item) => bucketKey(item) === letter);
      if (letterItems.length > 0) {
        groupedData[letter] = letterItems;
      }
    });
    NUMBERS.forEach((num) => {
      const numItems = items.filter((item) => bucketKey(item) === num);
      if (numItems.length > 0) {
        groupedData[num] = numItems;
      }
    });
    return groupedData;
  }, [cityItems, allCategories, catId, locale]);

  const cityOptions = useMemo(() => collectCityOptions(allItems), [allItems]);

  const categoryNavLevels = useMemo(() => {
    return getCategoryNavLevels(allCategories, catId)
      .map((level) => ({
        ...level,
        items: level.items.filter((cat) =>
          categoryHasMatchingItems(cityItems, allCategories, cat.id)
        ),
      }))
      .filter((level, index) => index === 0 || level.items.length > 0);
  }, [allCategories, catId, cityItems]);

  useEffect(() => {
    if (!catId || loading || allCategories.length === 0) return;
    if (categoryHasMatchingItems(cityItems, allCategories, catId)) return;
    const params = new URLSearchParams(window.location.search);
    if (selectedCity) params.set('city', selectedCity);
    else params.delete('city');
    const query = params.toString();
    navigate(query ? `/maddeler?${query}` : '/maddeler', { replace: true });
  }, [catId, loading, cityItems, allCategories, selectedCity, navigate]);

  const visibleLetters = (selectedGroup ? LETTER_GROUPS[selectedGroup] : ALPHABET).filter(
    (letter) => data[letter]?.length > 0
  );

  useEffect(() => {
    if (selectedGroup && !LETTER_GROUPS[selectedGroup].some((letter) => data[letter]?.length)) {
      setSelectedGroup(null);
      setShowAllArticles(true);
    } else if (!showAllArticles && selectedLetter && !data[selectedLetter]?.length) {
      setShowAllArticles(true);
    }
  }, [data, selectedGroup, selectedLetter, showAllArticles]);

  const rootCategories = getRootCategories(allCategories);
  const filteredCount = Object.values(data || {}).reduce(
    (sum, list) => sum + (Array.isArray(list) ? list.length : 0),
    0
  );
  const visibleCount = showAllArticles
    ? filteredCount
    : (data[selectedLetter]?.length || 0);
  const hasGroupedEntries = filteredCount > 0;
  const listHeading = selectedCity || t('nav.entriesAll');
  const listCountLabel = t('entriesPage.resultsCount').replace('{count}', String(visibleCount));

  return (
    <div className="articles-container">
      <div className="entries-filters">
        <div className="city-filter">
          <label htmlFor="entries-city-filter">{t('entriesPage.cityLabel')}</label>
          <select
            id="entries-city-filter"
            value={selectedCity}
            onChange={handleCityChange}
            aria-label={t('entriesPage.cityLabel')}
          >
            <option value="">{t('entriesPage.allCities')}</option>
            {cityOptions.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>

        <div className="entries-filters__refine">
          {rootCategories.length > 0 && (
            <div className="category-nav-stack">
              {categoryNavLevels.map((level, levelIndex) => (
                <div
                  key={level.parentId == null ? 'roots' : `level-${level.parentId}`}
                  className={`category-nav${levelIndex > 0 ? ' category-nav--sub' : ''}`}
                >
                  {levelIndex === 0 && (
                    <Link
                      className={`category-item ${catId === undefined ? 'active' : ''}`}
                      to={entriesPath('/maddeler')}
                    >
                      {t('entriesPage.all')}
                    </Link>
                  )}
                  {level.items.map((cat) => (
                    <Link
                      key={cat.id}
                      className={`category-item ${
                        isCategoryInSelection(allCategories, catId, cat.id) ? 'active' : ''
                      }`}
                      to={entriesPath(`/maddeler/kategori/${cat.id}`)}
                    >
                      {getLocalizedCategoryName(cat, locale)}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div className="alphabet-nav">
            <span
              className={`letter ${showAllArticles ? 'active' : ''}`}
              onClick={() => setShowAllArticles(true)}
            >
              {t('entriesPage.all')}
            </span>
            {visibleLetters.map((letter) => (
              <span
                key={letter}
                className={`letter ${selectedLetter === letter && !showAllArticles ? 'active' : ''}`}
                onClick={() => handleLetterClick(letter)}
              >
                {letter}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="articles-content">
        {loading ? (
          <div className="articles-loading" role="status" aria-live="polite">
            <Spinner animation="border" aria-hidden="true" />
            <p>{t('entriesPage.loading')}</p>
          </div>
        ) : !hasGroupedEntries ? (
          <div className="articles-empty" role="status">
            <p>{t(selectedCity ? 'entriesPage.noEntriesForFilters' : 'entriesPage.noEntriesInCategory')}</p>
          </div>
        ) : showAllArticles ? (
          // Tüm maddeler gösterimi
          <div className="all-articles-section">
            <div className="letter-title">
              <span className="letter-title__text">{listHeading}</span>
              <span className="entries-count">{listCountLabel}</span>
            </div>
            <img
              src="/images/cdtst/dekoratif.png"
              alt={t('entriesPage.decorativeLineAlt')}
              className="decorative-line"
            />
            {ALPHABET.concat(NUMBERS).map((letter) => 
              data[letter] && data[letter].length > 0 ? (
                <div key={letter} className="letter-group-section">
                  <h3 className="letter-group-title">{letter}</h3>
                  <div className="articles-list">
                    {data[letter].map((article) => {
                      const labels = getLocalizedItemLabels(article, locale);
                      return (
                      <Link
                        to={`/article/${article.id}`}
                        key={article.id}
                        className="article-link"
                        state={{
                          articleInfo: {
                            id: article.id,
                            title: article.title,
                            englishTitle: article.definition,
                            years: article.years,
                            content: article.content,
                            image: article.primaryImage
                          }
                        }}
                      >
                        <div className="article-card">
                          <h3>{labels.title}</h3>
                        </div>
                      </Link>
                      );
                    })}
                  </div>
                </div>
              ) : null
            )}
          </div>
        ) : (
          // Tekil harf gösterimi
          <div className="letter-section">
            <div className="letter-title">
              <span className="letter-title__text">{selectedLetter}</span>
              <span className="entries-count">{listCountLabel}</span>
            </div>
            <img
              src="/images/cdtst/dekoratif.png"
              alt={t('entriesPage.decorativeLineAlt')}
              className="decorative-line"
            />
            <div className="articles-list">
              {data[selectedLetter]?.map((article) => {
                const labels = getLocalizedItemLabels(article, locale);
                return (
                <Link
                  to={`/article/${article.id}`}
                  key={article.id}
                  className="article-link"
                  state={{
                    articleInfo: {
                      id: article.id,
                      title: article.title,
                      englishTitle: article.definition,
                      years: article.years,
                      content: article.content,
                      image: article.primaryImage
                    }
                  }}
                >
                  <div className="article-card">
                    <h3>{labels.title}</h3>
                  </div>
                </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Articles; 